import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  FileText,
  Package,
  ChefHat,
  BarChart3,
  X,
  ScanLine,
  Check,
  Scissors,
} from 'lucide-react';

interface FlowStep {
  id: string;
  icon: React.ElementType;
  label: string;
  shortLabel: string;
  paths: string[];
  href: string;
  color: string;
  bgColor: string;
}

const FLOWS: Record<string, FlowStep[]> = {
  scan: [
    { id: 'scan', icon: ScanLine, label: 'Escanear carta', shortLabel: 'Carta', paths: ['/ingredients'], href: '/ingredients?scan=carta', color: '#0891b2', bgColor: '#ecfeff' },
    { id: 'ingredients', icon: Package, label: 'Ingredientes', shortLabel: 'Ingredientes', paths: ['/ingredients'], href: '/ingredients', color: '#d97706', bgColor: '#fffbeb' },
    { id: 'despiece', icon: Scissors, label: 'Despieces', shortLabel: 'Despieces', paths: ['/despieces'], href: '/despieces', color: '#e11d48', bgColor: '#fff1f2' },
    { id: 'recipe', icon: ChefHat, label: 'Recetas', shortLabel: 'Recetas', paths: ['/recipes'], href: '/recipes/new', color: '#ea580c', bgColor: '#fff7ed' },
    { id: 'foodcost', icon: BarChart3, label: 'Rentabilidad', shortLabel: 'Food cost', paths: ['/dashboard'], href: '/dashboard', color: '#16a34a', bgColor: '#f0fdf4' },
  ],
  manual: [
    { id: 'carta', icon: FileText, label: 'Crear carta', shortLabel: 'Carta', paths: ['/cartas'], href: '/cartas', color: '#9333ea', bgColor: '#faf5ff' },
    { id: 'ingredients', icon: Package, label: 'Ingredientes', shortLabel: 'Ingredientes', paths: ['/ingredients'], href: '/ingredients/new', color: '#d97706', bgColor: '#fffbeb' },
    { id: 'despiece', icon: Scissors, label: 'Despieces', shortLabel: 'Despieces', paths: ['/despieces'], href: '/despieces', color: '#e11d48', bgColor: '#fff1f2' },
    { id: 'recipe', icon: ChefHat, label: 'Recetas', shortLabel: 'Recetas', paths: ['/recipes'], href: '/recipes/new', color: '#ea580c', bgColor: '#fff7ed' },
    { id: 'foodcost', icon: BarChart3, label: 'Rentabilidad', shortLabel: 'Food cost', paths: ['/dashboard'], href: '/dashboard', color: '#16a34a', bgColor: '#f0fdf4' },
  ],
  ocr: [
    { id: 'ocr', icon: ScanLine, label: 'Escanear albarán', shortLabel: 'OCR', paths: ['/ingredients'], href: '/ingredients?scan=true', color: '#0891b2', bgColor: '#ecfeff' },
    { id: 'ingredients', icon: Package, label: 'Ingredientes', shortLabel: 'Ingredientes', paths: ['/ingredients'], href: '/ingredients', color: '#d97706', bgColor: '#fffbeb' },
    { id: 'despiece', icon: Scissors, label: 'Despieces', shortLabel: 'Despieces', paths: ['/despieces'], href: '/despieces', color: '#e11d48', bgColor: '#fff1f2' },
    { id: 'recipe', icon: ChefHat, label: 'Recetas', shortLabel: 'Recetas', paths: ['/recipes'], href: '/recipes/new', color: '#ea580c', bgColor: '#fff7ed' },
    { id: 'foodcost', icon: BarChart3, label: 'Rentabilidad', shortLabel: 'Food cost', paths: ['/dashboard'], href: '/dashboard', color: '#16a34a', bgColor: '#f0fdf4' },
  ],
  default: [
    { id: 'ingredients', icon: Package, label: 'Ingredientes', shortLabel: 'Ingredientes', paths: ['/ingredients'], href: '/ingredients/new', color: '#d97706', bgColor: '#fffbeb' },
    { id: 'despiece', icon: Scissors, label: 'Despieces', shortLabel: 'Despieces', paths: ['/despieces'], href: '/despieces', color: '#e11d48', bgColor: '#fff1f2' },
    { id: 'recipe', icon: ChefHat, label: 'Recetas', shortLabel: 'Recetas', paths: ['/recipes'], href: '/recipes/new', color: '#ea580c', bgColor: '#fff7ed' },
    { id: 'carta', icon: FileText, label: 'Carta', shortLabel: 'Carta', paths: ['/cartas'], href: '/cartas', color: '#9333ea', bgColor: '#faf5ff' },
    { id: 'foodcost', icon: BarChart3, label: 'Rentabilidad', shortLabel: 'Food cost', paths: ['/dashboard'], href: '/dashboard', color: '#16a34a', bgColor: '#f0fdf4' },
  ],
};

function getFlowType(): string | null {
  try {
    const profile = JSON.parse(localStorage.getItem('pleko-onboarding-profile') || 'null');
    if (!profile) return null;
    if (profile.hasCarta === 'scan') return 'scan';
    if (profile.hasCarta === 'manual') return 'manual';
    if (profile.hasInvoices === 'ocr') return 'ocr';
    return 'default';
  } catch {
    return null;
  }
}

export default function OnboardingFlowBar() {
  const { pathname } = useLocation();
  const [dismissed, setDismissed] = useState(() =>
    localStorage.getItem('pleko-flowbar-dismissed') === 'true'
  );
  const [flowType, setFlowType] = useState<string | null>(null);

  useEffect(() => {
    setFlowType(getFlowType());
  }, []);

  if (dismissed || !flowType) return null;

  const steps = FLOWS[flowType] || FLOWS.default;

  let currentIndex = steps.findIndex((s) =>
    s.paths.some((p) => pathname.startsWith(p))
  );
  if (currentIndex === -1) currentIndex = 0;

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('pleko-flowbar-dismissed', 'true');
  };

  return (
    <div className="bg-white border-b border-slate-100">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-2.5">
        <div className="flex items-center gap-2 sm:gap-3">
          {/* Label - desktop only */}
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest hidden lg:inline flex-shrink-0">
            Tu ruta
          </span>

          {/* Stepper */}
          <div className="flex items-center flex-1 min-w-0">
            {steps.map((step, index) => {
              const isActive = index === currentIndex;
              const isDone = index < currentIndex;
              const StepIcon = step.icon;
              const isLast = index === steps.length - 1;

              return (
                <div key={step.id} className="flex items-center flex-1 min-w-0 last:flex-none">
                  {/* Step node */}
                  <Link
                    to={step.href}
                    className="flex flex-col items-center gap-1 group relative flex-shrink-0"
                  >
                    {/* Circle */}
                    <div
                      className={`relative w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center transition-all duration-500 ${
                        isActive ? 'scale-110' : isDone ? '' : 'opacity-50 group-hover:opacity-80'
                      }`}
                      style={{
                        background: isDone ? '#22c55e' : isActive ? step.color : '#e2e8f0',
                        boxShadow: isActive
                          ? `0 0 0 3px ${step.bgColor}, 0 0 16px ${step.color}40`
                          : 'none',
                      }}
                    >
                      {isDone ? (
                        <Check className="w-4 h-4 text-white" strokeWidth={3} />
                      ) : (
                        <StepIcon className="w-4 h-4 text-white" />
                      )}

                      {/* Pulse ring on active */}
                      {isActive && (
                        <span
                          className="absolute inset-0 rounded-full"
                          style={{
                            border: `2px solid ${step.color}`,
                            animation: 'onb-ping 2s cubic-bezier(0,0,0.2,1) infinite',
                          }}
                        />
                      )}
                    </div>

                    {/* Label */}
                    <span
                      className={`text-[10px] sm:text-[11px] font-semibold leading-tight text-center transition-colors duration-300 max-w-[60px] sm:max-w-none truncate ${
                        isActive ? 'text-slate-900' : isDone ? 'text-green-600' : 'text-slate-400'
                      }`}
                    >
                      <span className="hidden sm:inline">{step.label}</span>
                      <span className="sm:hidden">{step.shortLabel}</span>
                    </span>

                    {/* "Estás aquí" badge on active */}
                    {isActive && (
                      <span
                        className="absolute -top-5 left-1/2 -translate-x-1/2 whitespace-nowrap text-[9px] font-bold px-1.5 py-0.5 rounded-full text-white"
                        style={{ background: step.color }}
                      >
                        Aquí
                      </span>
                    )}
                  </Link>

                  {/* Connector line */}
                  {!isLast && (
                    <div className="flex-1 mx-1 sm:mx-1.5 h-0.5 rounded-full relative overflow-hidden min-w-[12px]"
                      style={{ background: '#e2e8f0' }}>
                      <div
                        className="absolute inset-y-0 left-0 rounded-full transition-all duration-700 ease-out"
                        style={{
                          width: isDone ? '100%' : isActive ? '50%' : '0%',
                          background: isDone
                            ? '#22c55e'
                            : isActive
                            ? `linear-gradient(90deg, ${step.color}, ${step.color}00)`
                            : undefined,
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Close */}
          <button
            onClick={handleDismiss}
            className="p-1 text-slate-300 hover:text-slate-500 transition-colors flex-shrink-0 rounded-full hover:bg-slate-50"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <style>{`
        @keyframes onb-ping {
          0% { transform: scale(1); opacity: 0.6; }
          75%, 100% { transform: scale(1.5); opacity: 0; }
        }
      `}</style>
    </div>
  );
}
